import type { AiCapabilityPage } from "../schema.js";
import { ALL_ROLES, LIVE, SLOT_GROUP, WRITE_FLAGS } from "./_shared.js";

/**
 * `PAY-ON-BEHALF` (@UC0218) — chat T2: **đóng hộ suất người khác** (QĐ PO 17/09 #5, `US-AIV2-0058`).
 *
 * 🔴🔴 CỬA GUI CÓ **HAI BƯỚC, HAI NGƯỜI** — và trang này phục vụ **cả hai** (QĐ PO 22/09):
 *   1. **A đề xuất** nộp hộ suất của B ⇒ `PayOnBehalf(proposed)` + outbox, **0 BÚT TOÁN**;
 *   2. **B trả lời** ⇒ `accept` thì mới post CONTRIBUTION + `DebtEntry(on_behalf)` (B nợ A);
 *      `decline` thì **0 ledger, 0 debt**.
 *
 * Đây chính là §3.7 được cửa GUI tôn trọng sẵn: *"khai báo trả"* (bước 1) tách khỏi *"xác nhận nhận"*
 * (bước 2), và **chỉ bước 2 đổi số dư**. Trang KHÔNG gộp hai bước — gộp là đúng cái §3.7 cấm.
 *
 * 🔴 VAI = **mọi member** (`ALL_ROLES`), không phải O/A/T: đóng hộ là việc giữa hai thành viên
 * với nhau. Cửa GUI `propose` cũng chỉ gác `UserAuthGuard` + guard ngữ cảnh (người trả phải là member
 * ACTIVE của quỹ) — mời hẹp hơn cửa là giấu một năng lực người dùng có quyền dùng.
 *
 * 🔴 Ô `quyetDinh` phân nhánh giữa hai việc, nhưng **hai việc cần hai ô khác nhau**:
 *   · *"tôi đóng hộ"* cần ô `nguoi` (đóng hộ AI) + `khoan` (khoản nào);
 *   · *"tôi đồng ý"* cần biết **lời đề xuất nào** — và người trả lời là B, nên `nguoi` ở đây là
 *     **người đề xuất**, không phải người được nộp hộ.
 * ⇒ Cả hai ô là **tuỳ chọn ở Sổ**, và binding fail-loud theo từng nhánh. Khai `required: true` cho cả
 * hai là bắt nhánh này điền ô của nhánh kia.
 *
 * 🔴 KÊNH XEM — nhánh `propose` hỏi *"tôi đóng hộ được cho AI"*, nên nó phải đọc **tên người khác +
 * số họ còn nợ**. Đó là danh sách "ai chưa đóng" mà §7 P5.3 giao cho TỪNG NHÓM quyết
 * (`payment_status_visibility`), và cửa GUI của việc này chỉ có hai route **GHI** ⇒ chat là **cửa ĐỌC
 * mới**, không phải parity. `PayOnBehalfService.listPayableForChat` gác CÙNG khoá, dùng CHUNG
 * `PAYMENT_STATUS_MANAGER_ROLES` + `resolvePaymentStatusVisibility` với
 * `MembersPaymentStatusService` — không cơ chế thứ hai (§3.8). Nhóm để `managers_only` ⇒ member
 * thường không dò được ai còn nợ qua trang này; vai quản trị vẫn thấy.
 */
export const PAY_ON_BEHALF = {
  id: "PAY-ON-BEHALF",
  uc: "@UC0218",
  tier: "T2",
  status: LIVE,
  roles: ALL_ROLES,
  flags: WRITE_FLAGS,
  slots: [
    SLOT_GROUP,
    {
      id: "quyetDinh",
      type: "enum",
      required: true,
      ask: "Bạn muốn đóng hộ cho ai, hay trả lời một lời đề nghị đóng hộ?",
      // Chữ NGƯỜI ĐỌC, không phải `propose`/`accept`/`decline` — phiếu nội suy `{quyetDinh}` nên người
      // dùng phải đọc được nó (tiền lệ #811 M1).
      choices: ["đóng hộ", "đồng ý", "từ chối"],
      // review #1036 HIGH-2: "không/chưa đồng ý" từng khớp NHÃN "đồng ý" ⇒ nhận đóng hộ. Câu phủ định/nghi vấn không bao giờ ra "đồng ý"/"đóng hộ".
      guardedChoices: ["đồng ý", "đóng hộ"],
      /*
       * 🔴 UX-POB-WORDING (@UC0218 · GUIDE-UX2 V-2 mục 3) — nút thẻ «Trả lời đề nghị đóng hộ» điền ô `nguoi` = suất của chính mình ⇒ binding chỉ còn mời
       * hai lựa chọn TRẢ LỜI (`decisionCandidates`, MED-7(a)) ⇒ câu hỏi của nhánh trả lời, không «đóng hộ cho ai». Khoản (`{khoan}` — nhãn đợt nút đã
       * điền) giúp người nhiều khoản biết đề nghị nào; thiếu nhãn ⇒ câu không tên. Đủ ba lựa chọn (mở từ menu) ⇒ `ask` như cũ.
       */
      offeredAsks: [
        { choices: ["đồng ý", "từ chối"], ask: "Có người đề nghị đóng hộ khoản “{khoan}” của bạn. Bạn đồng ý hay từ chối?" },
        { choices: ["đồng ý", "từ chối"], ask: "Có người đề nghị đóng hộ khoản của bạn. Bạn đồng ý hay từ chối?" },
      ],
    },
    // 🔴 Tham chiếu tới người ĐANG CÓ ⇒ 0 hoặc ≥2 khớp thì HỎI, không đoán (CLAUDE.md §4, cam kết #4).
    {
      id: "nguoi",
      type: "member",
      required: false,
      // `ask` giữ NGUYÊN (nó vào prompt của Người phiên dịch — nghĩa của ô ở CẢ hai nhánh); câu người dùng THẤY theo nhánh ở `branchAsks`.
      ask: "Đóng hộ cho ai? (hoặc: ai đề nghị đóng hộ bạn?)",
      /*
       * 🔴 AI-MENU-POB-ENTRY (@UC0218 · GOPPY-45, đo 28/10): đã chọn «đóng hộ» mà câu vẫn kèm vế «(hoặc: ai đề nghị đóng hộ bạn?)» — vế của nhánh trả lời.
       * Nhánh đề nghị: nút = suất đóng hộ được (`slotCandidates` của binding). Nhánh trả lời (≥2 lời đề nghị — nút là người đề nghị HOẶC suất được đóng
       * hộ, `answerSlotValues`): câu hỏi lời đề nghị nào, chèn đúng quyết định đã chọn.
       */
      branchAsks: [
        { slot: "quyetDinh", choices: ["đóng hộ"], ask: "Đóng hộ cho ai?" },
        { slot: "quyetDinh", choices: ["đồng ý", "từ chối"], ask: "Bạn {quyetDinh} lời đề nghị đóng hộ nào?" },
      ],
    },
    { id: "khoan", type: "collection", required: false, ask: "Khoản nào?" },
  ],
  llm: {
    description: "Đề nghị đóng tiền hộ suất của một thành viên khác, hoặc trả lời một lời đề nghị đóng hộ mình.",
    examples: [
      "tôi đóng hộ tiền quỹ cho chị Lan",
      "toi dong ho tien quy cho chi Lan",
      "mình nộp giùm suất của Bình",
      "minh nop gium suat cua Binh",
      "đóng hộ cho bé An tháng này",
      "dong ho cho be An thang nay",
      "đồng ý cho anh Nam đóng hộ",
      "dong y cho anh Nam dong ho",
      "từ chối đề nghị đóng hộ",
      "tu choi de nghi dong ho",
      "ai đó đóng hộ tôi, tôi đồng ý",
      "tôi trả giùm suất của Minh",
      // ≥2 câu "nói tắt" (`SHORTHAND_RE`: số tiền viết tắt · từ khoá ngắn) — người dùng thật gõ thế.
      "ck hộ 200k cho Lan",
      "dong ho 500k suat cua Binh",
      // ——— 0105: thêm cho đủ 40 ———
      "ai do dong ho toi toi dong y",
      "toi tra gium suat cua Minh",
      "tôi nộp hộ phần của Hùng tháng này",
      "toi nop ho phan cua Hung thang nay",
      "cho mình đóng hộ suất của bé An",
      "cho minh dong ho suat cua be An",
      "tôi trả thay cho Lan đợt thu này",
      "toi tra thay cho Lan dot thu nay",
      "đóng giùm hàng xóm một suất",
      "dong gium hang xom mot suat",
      "ok, Bình đóng hộ tôi cũng được",
      "ok Binh dong ho toi cung duoc",
      "đồng ý với đề nghị đóng hộ",
      "dong y voi de nghi dong ho",
      "tôi không đồng ý cho ai đóng hộ",
      "toi khong dong y cho ai dong ho",
      "từ chối lời đề nghị nộp hộ của Minh",
      "tu choi loi de nghi nop ho cua Minh",
      "mình muốn đóng hộ cho một bạn",
      "minh muon dong ho cho mot ban",
      "đóng hộ quỹ cho người khác thì làm sao?",
      "dong ho quy cho nguoi khac thi lam sao",
      "trả hộ tiền quỹ cho An 300k",
      "tra ho tien quy cho An 300k",
      "nộp hộ",
      "nop ho",
    ],
    // AI-INTERP M12 (QĐ I12): câu THỬ — KHÔNG vào prompt/BM25; viết đời thường, không chép ví dụ (validator HELD_OUT_IS_EXAMPLE).
    heldOutExamples: [
      "tui dong gium phan cua chi Hai luon",
      "để em trả hộ suất bé Na tháng này ạ",
      "Nam muon dong ho tui ha, ok luon",
      "ko can ai dong ho tui dau, tu choi nha",
      "cô đóng thay cho cháu Minh đợt này",
      "minh ck dum suat cua anh Tu",
    ],
    // 🔴 US-AIV2-0111 C06 (@UC0320) — câu HỎI (việc XEM) không thuộc trang ghi này; mỗi câu là ví dụ của một trang đọc ⇒ eval đo được.
    questionsNotHere: ["có việc gì đang chờ tôi không?"],
  },
  executor: "payOnBehalf.decide",
  /**
   * `@UC0216` — **RỖNG có chủ đích**, và đây là lời khai chứ không phải chỗ bỏ sót.
   *
   * `endpoints` khai *"cửa **của `apps/mobile`** mà trang chịu trách nhiệm"*, và gate
   * `assert-ai-coverage` tự khám phá hai đầu — khai một cửa mobile **chưa gọi** ⇒ ĐỎ.
   * Đo: `grep "pay-on-behalf|payOnBehalf"` trong `apps/mobile/lib` ⇒ **0 caller**. Hai cửa
   * `POST /member-dues/{id}/pay-on-behalf` và `POST /pay-on-behalf/{id}/respond` **chưa có màn nào**.
   *
   * ⇒ Trang chat là **caller đầu tiên** của cả hai — hợp lệ (AI đi trước GUI, cùng tư thế `/reject`
   * của `EXP-APPROVE` ở 0055), nhưng chúng **không thuộc bản đồ cửa-mobile** cho tới khi mobile dựng màn.
   * 🔁 Ngày mobile thêm hai cửa đó: khai vào đây, và **dùng chung** — không mở cửa thứ hai.
   */
  endpoints: [],
  facts: ["viecSeLam", "viecDaLam", "tenNguoi", "tenKhoan", "tenNguoiDongHo", "soTien", "ketQua"],
  templates: {
    // 0110 S13 (@UC0255): câu mở bằng chữ cố định — ô quyết định là chữ thường của `choices`, mở câu bằng nó là chữ thường đầu câu.
    // 🔴 G-3 vòng FIX HIGH-1 (@UC0218): `{tenNguoi}` LUÔN là CHỦ SUẤT — nhánh đề nghị: người được đóng hộ; nhánh trả lời: suất (bé) được đóng hộ (bà
    // đề nghị cho hai bé cùng đợt ⇒ hai thẻ khác nhau ở đúng chỗ này — AI-INV-4). Người đề nghị ở nhánh trả lời đi vào ĐOẠN TUỲ CHỌN «— người đóng hộ:
    // {tenNguoiDongHo}»; nhánh đề nghị người đóng hộ là chính người bấm ⇒ rỗng ⇒ đoạn + dòng biến mất. Đoạn tuỳ chọn chỉ ở `confirm` (validator Sổ).
    // 🔴 Vòng FIX #1440 HIGH-1 (§3.7/P2.1): câu VIỆC theo nhánh (`viecSeLam`/`viecDaLam` — binding) thay `{quyetDinh}` trần: nhánh đề nghị từng ra
    // «Đã đóng hộ: suất …» (nói tiền đã đóng khi mới là lời đề nghị, 0 bút toán). Nhánh trả lời giữ nguyên chữ («đồng ý»/«Đã đồng ý»).
    confirm: "Xác nhận {viecSeLam}: suất “{tenKhoan}” của {tenNguoi}[[ — người đóng hộ: {tenNguoiDongHo}]], {soTien}?",
    done: "{viecDaLam}: suất “{tenKhoan}” của {tenNguoi} ({soTien}). {ketQua}",
  },
  labels: {
    quyetDinh: "Việc",
    viecSeLam: "Việc",
    viecDaLam: "Việc",
    tenNguoi: "Người",
    tenKhoan: "Khoản",
    tenNguoiDongHo: "Người đóng hộ",
    soTien: "Số tiền",
    /**
     * 🔴 BA kết cục, và chúng khác nhau về TIỀN: đề xuất ⇒ **0 bút toán, chờ người kia**; đồng ý ⇒ tiền
     * đã vào sổ **và sinh nợ**; từ chối ⇒ **không có gì xảy ra**. Một câu `done` chung cho cả ba là nói
     * sai với hai trong ba (cùng lớp lỗi đã vá ở `EXP-F-NOALLOC` #871 · `EXP-F-EQUAL` #905 · 0055).
     */
    ketQua: "Kết quả",
  },
  journey: { outcomes: { done: { next: ["READ-PEOPLE-MINE"] } } },
  help: { title: "Đóng tiền hộ thành viên", tryNow: "tôi đóng hộ tiền quỹ cho chị Lan" },
} satisfies AiCapabilityPage;
